import React from 'react';
import type { LikertResponse, QuizQuestion } from '../../types/investmentPhilosophy';

interface IPQuestionCardProps {
  question: QuizQuestion;
  index: number;
  total: number;
  selected: LikertResponse | null;
  canGoBack: boolean;
  onAnswer: (response: LikertResponse) => void;
  onBack: () => void;
}

const OPTIONS: { value: LikertResponse; label: string }[] = [
  { value: 5, label: 'Strongly Agree' },
  { value: 4, label: 'Agree' },
  { value: 3, label: 'Neutral' },
  { value: 2, label: 'Disagree' },
  { value: 1, label: 'Strongly Disagree' },
];

export const IPQuestionCard: React.FC<IPQuestionCardProps> = ({
  question,
  index,
  total,
  selected,
  canGoBack,
  onAnswer,
  onBack,
}) => {
  return (
    <div
      key={question.id}
      className="flex-1 flex flex-col min-h-0 animate-fade-up opacity-0"
    >
      {/* Question number */}
      <div className="text-center mt-3 mb-3">
        <span
          className="font-mono text-xs uppercase tracking-wider"
          style={{ color: 'hsl(var(--muted-foreground))' }}
        >
          {index + 1} / {total}
        </span>
      </div>

      {/* Prompt — sits near the top */}
      <div className="px-2 pb-4 sm:pb-6">
        <h2
          className="font-serif text-xl sm:text-2xl text-center leading-snug"
          style={{ color: 'hsl(var(--foreground))' }}
        >
          {question.text}
        </h2>
      </div>

      {/* Likert pills, top-to-bottom: Strongly Agree → Strongly Disagree */}
      <div className="space-y-2 sm:space-y-3">
        {OPTIONS.map((opt) => {
          const isSelected = selected === opt.value;
          return (
            <button
              key={opt.value}
              onClick={() => {
                if ('vibrate' in navigator) {
                  try {
                    navigator.vibrate(10);
                  } catch {
                    // ignore
                  }
                }
                onAnswer(opt.value);
              }}
              className="w-full rounded-full px-6 py-3 text-base font-medium transition-all active:scale-[0.98]"
              style={{
                backgroundColor: isSelected
                  ? 'var(--clue-green-accent)'
                  : 'var(--clue-bg-card)',
                color: isSelected ? '#fff' : 'hsl(var(--foreground))',
                border: `1px solid ${
                  isSelected ? 'var(--clue-green-accent)' : 'var(--clue-border-light)'
                }`,
                boxShadow: 'var(--clue-shadow-sm)',
              }}
            >
              {opt.label}
            </button>
          );
        })}
      </div>

      {/* Back link */}
      <div className="flex justify-center pt-3 min-h-[2rem]">
        {canGoBack && (
          <button
            onClick={onBack}
            className="text-sm hover:opacity-80 transition-opacity"
            style={{ color: 'hsl(var(--muted-foreground))' }}
          >
            ← Back
          </button>
        )}
      </div>

      {/* Spacer — pushes empty space to the bottom instead of centering content */}
      <div className="flex-1" />
    </div>
  );
};
